<ng-container *ngIf="aiSub$ | async as aiSub">
  <div #searchWrapper>
    <mat-form-field2
      class="search mat-block navbar-search-box"
      [showLabel]="false"
      (click)="isMenuOpen = true"
      cdkOverlayOrigin
      #dropdownTrigger="cdkOverlayOrigin"
    >
      <input
        matInput
        i18n-matTooltip
        matTooltip="Smart Search powered by AI — type naturally to find properties, guests, bookings, or hold events. No exact keywords needed!"
        matTooltipPosition="below"
        [formControl]="searchCtrl"
        autocomplete="off"
        (focus)="isFocused = true"
        (focusout)="isFocused = false"
        i18n-placeholder
        placeholder="Search..."
        type="text"
        class="search-dropdown"
        [disabled]="isLoading"
      />
      <i matPrefix class="cs-icon cs-icon-ai-search text-xxl" *ngIf="!isLoading"></i>
      <i matPrefix class="far fa-spinner fa-spin text-info" *ngIf="isLoading"></i>
      <i
        matSuffix
        *ngIf="searchCtrl.value?.length > 0"
        (click)="$event.stopPropagation(); onClearSearch()"
        class="far fa-times"
      ></i>
      <a
        matSuffix
        voiceRecord
        #recorder="voiceRecord"
        (voice)="onVoice($event)"
        class="ml-2"
        *ngIf="aiSub.isValid"
        [class.disabled]="isLoading"
      >
        <i
          class="fas fa-microphone text-info"
          *ngIf="!recorder.isRecording"
          matTooltip="Voice to text"
          i18n-matTooltip
        ></i>
        <i class="fas fa-dot-circle text-danger fa-beat-fade" *ngIf="recorder.isRecording"></i>
      </a>
      <a
        matSuffix
        class="ml-2"
        *ngIf="!aiSub.isValid"
        (click)="$event.stopPropagation(); onEnableAI()"
        matTooltip="To use AI Search, please subscribe via the App Store."
        i18n-matTooltip
        style="filter: grayscale(100%)"
      >
        <i class="cs-icon cs-icon-ai"></i>
      </a>
    </mat-form-field2>
  </div>
  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayWidth]="searchWrapper.offsetWidth"
    [cdkConnectedOverlayFlexibleDimensions]="true"
    (overlayOutsideClick)="isMenuOpen = false"
    [cdkConnectedOverlayMinWidth]="500"
    [cdkConnectedOverlayPositions]="[{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]"
    [cdkConnectedOverlayOrigin]="dropdownTrigger"
    [cdkConnectedOverlayOpen]="isMenuOpen"
  >
    <div class="bg-white shadow2 border rounded overflow-hidden w-100" (delayClick)="isMenuOpen = false">
      <ng-template [ngTemplateOutlet]="loading" *ngIf="isLoading"></ng-template>
      <ng-container *ngIf="!isLoading">
        <ng-template [ngTemplateOutlet]="empty" *ngIf="isResultsEmpty"></ng-template>
        <ng-template [ngTemplateOutlet]="searchResults" *ngIf="!isResultsEmpty"></ng-template>
      </ng-container>
      <ng-template [ngTemplateOutlet]="searchSuggestions" *ngIf="aiSub.isValid"></ng-template>
    </div>
  </ng-template>

  <ng-template #searchResults>
    <div class="px-3 py-2 border-bottom" style="margin-bottom: -1px" *ngIf="aiSub.isValid">
      {{ assistantMessage }}
    </div>
    <div class="overflow-auto" style="max-height: 60vh">
      <div *ngFor="let data of resultsByType | keyvalue">
        <ng-container [ngSwitch]="data.key">
          <div *ngSwitchCase="types.Booking" class="border-top">
            <div class="d-flex align-items-center justify-content-between p-2">
              <div class="text-sm text-semi-bold text-light">Bookings ({{ data.value?.length }})</div>
              <a (click)="openSearchResultsDialog($any(types).Booking)" class="text-sm" i18n>view all</a>
            </div>
            <ul class="list-unstyled mb-0">
              <li
                *ngFor="let item of data.value.slice(0, 5)"
                (click)="onInquiryDetails(item.pkey)"
                class="d-flex align-items-center gap-2 p-2"
              >
                <app-rental-image [rentalId]="item.rental_id" [size]="25" [circle]="true"></app-rental-image>
                <div>
                  <app-rental-name [rentalId]="item.rental_id"></app-rental-name>
                  <div class="text-muted text-sm">
                    {{ item.guest_arrive | epochUTC : 'DD MMM, YYYY' }} -
                    {{ item.guest_depart | epochUTC : 'DD MMM, YYYY' }} •
                    {{ (item.num_adults || 0) + (item.num_child || 0) }}
                    <ng-container i18n>Guests</ng-container>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div *ngSwitchCase="types.Invoice" class="border-top">
            <div class="d-flex align-items-center justify-content-between p-2">
              <div class="text-sm text-semi-bold text-light">Invoices ({{ data.value?.length }})</div>
              <a (click)="openSearchResultsDialog($any(types).Invoice)" class="text-sm" i18n>view all</a>
            </div>
            <ul class="list-unstyled mb-0">
              <li
                *ngFor="let item of data.value.slice(0, 5)"
                (click)="onInvoiceDetails(item.pkey)"
                class="d-flex align-items-center gap-2 p-2"
              >
                <app-avatar [text]="item.title" [size]="25"></app-avatar>
                <div>
                  <div>{{ item.title }}</div>
                  <div class="text-muted text-sm">
                    {{ item.invoice_num }} • Due ({{ item.due_date | epochUTC : 'date' }}) •
                    <span class="badge badge-pill badge-secondary-info">{{ item.status | invoiceStatus }}</span>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div *ngSwitchCase="types.Guest" class="border-top">
            <div class="d-flex align-items-center justify-content-between p-2">
              <div class="text-sm text-semi-bold text-light">Guests ({{ data.value?.length }})</div>
              <a (click)="openSearchResultsDialog($any(types).Guest)" class="text-sm" i18n>view all</a>
            </div>
            <ul class="list-unstyled mb-0">
              <li
                *ngFor="let item of data.value.slice(0, 5)"
                (click)="onGuestDetails(item.guest_id)"
                class="d-flex align-items-center gap-2 p-2"
              >
                <app-avatar [text]="item.title" [size]="25"></app-avatar>
                <div>
                  <div>{{ item.title }} {{ item.status | invoiceStatus }}</div>
                  <div class="text-muted text-sm">
                    {{ item.primaryemail }} ({{ item.created | epoch : 'MMM YYYY' }})
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div *ngSwitchCase="types.Hold" class="border-top">
            <div class="d-flex align-items-center justify-content-between p-2">
              <div class="text-sm text-semi-bold text-light">Hold Events ({{ data.value?.length }})</div>
              <a (click)="openSearchResultsDialog($any(types).Hold)" class="text-sm" i18n>view all</a>
            </div>
            <ul class="list-unstyled mb-0">
              <li
                *ngFor="let item of data.value.slice(0, 5)"
                (click)="onHoldDetails(item)"
                class="d-flex align-items-center gap-2 p-2"
              >
                <app-rental-image [rentalId]="item.rental_id" [size]="25" [circle]="true"></app-rental-image>
                <div>
                  <div>{{ item.title }}</div>
                  <div class="text-muted text-sm">
                    {{ item.start | epochUTC : 'DD MMM, YYYY' }} -
                    {{ item.end | epochUTC : 'DD MMM, YYYY' }}
                    <ng-container *ngIf="calendarTypes[item.type] as type">
                      •
                      <span class="badge badge-pill badge-secondary-info"> {{ type.name }}</span>
                    </ng-container>
                  </div>
                </div>
              </li>
            </ul>
          </div>
          <div *ngSwitchCase="types.Rental" class="border-top">
            <div class="d-flex align-items-center justify-content-between p-2">
              <div class="text-sm text-semi-bold text-light">Rental ({{ data.value?.length }})</div>
              <a (click)="openSearchResultsDialog($any(types).Rental)" class="text-sm" i18n>view all</a>
            </div>
            <ul class="list-unstyled mb-0">
              <li
                *ngFor="let item of data.value.slice(0, 5)"
                class="d-flex align-items-center gap-2 p-2"
                (click)="onRentalDetails(item.pkey)"
              >
                <app-rental-image [rentalId]="item.pkey" [size]="25" [circle]="true"></app-rental-image>
                <div>
                  <app-rental-name [rentalId]="item.pkey"></app-rental-name>
                  <div class="text-muted text-sm">
                    {{ item.address | address : false }}
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </ng-container>
      </div>
    </div>
  </ng-template>

  <ng-template #searchSuggestions>
    <div class="border-top">
      <div class="d-flex align-items-center justify-content-between p-2">
        <div class="text-sm text-semi-bold text-light d-flex align-items-center">
          <ng-container i18n>Similar Questions</ng-container>
          &nbsp;
          <app-page-help articleId="jjnrBAgsrZmjhsKFCLENmV"></app-page-help>
        </div>
      </div>
      <ul class="list-unstyled mb-0">
        <li *ngFor="let item of randomQuestions" (click)="onSearch(item)" class="d-flex align-items-center gap-2 p-2">
          <i class="cs-icon cs-icon-ai-blink text-primary"></i> {{ item }}
        </li>
      </ul>
    </div>
  </ng-template>

  <ng-template #loading>
    <div class="d-flex flex-column justify-content-center align-items-center p-5">
      <i class="cs-icon cs-icon-ai-blink text-primary mb-4 fa-beat" style="font-size: 2rem"></i>
      <div i18n>Analyzing data...Hang tight</div>
      <div class="text-muted text-sm" i18n>AI is preparing the latest data for you.</div>
    </div>
  </ng-template>

  <ng-template #empty>
    <div class="d-flex flex-column justify-content-center align-items-center text-center p-5">
      <i class="cs-icon cs-icon-ai-blink text-primary mb-4" style="font-size: 2rem"></i>
      <div i18n>No results found</div>
      <div class="text-muted text-sm" i18n>{{ assistantMessage }}</div>
    </div>
  </ng-template>
</ng-container>
